import React, { useState } from 'react'
import styles from './styles.module.scss'

interface OpenBankingProps {
    openBankingData: any
    isLoading: boolean
    onLoadingChange: (loading: boolean) => void
}

export default function OpenBanking({ openBankingData, isLoading, onLoadingChange }: OpenBankingProps) {
    const [isClicked, setIsClicked] = useState(false)
    const [copySuccess, setCopySuccess] = useState(false)
    const buttonHref = openBankingData?.shortLink || openBankingData?.link || '#'

    const handleButtonClick = () => {
        setIsClicked(true)
        onLoadingChange(true)
    }

    const handleCopyLink = async () => {
        try {
            await navigator.clipboard.writeText(buttonHref)
            setCopySuccess(true)
            setTimeout(() => setCopySuccess(false), 2000)
        } catch (err) {
            console.error('Failed to copy link:', err)
        }
    }

    return (
        <div className={styles.openBankingContainer}>
            <h2 className={styles.headerTitle}>Complete Your Application with Open Banking</h2>
            <span className={styles.headerDivider}></span>
            <p className={styles.centeredText}>
                We use regulated Open Banking to check if the lender acted irresponsibly. Once connected, our open
                banking partner{' '}
                <strong>
                    <a href="https://boshhhfintech.com/" target="_blank" rel="noopener noreferrer">
                        Boshhh
                    </a>
                </strong>{' '}
                will analyse your lender behaviour to assess if you are owed money because of their irresponsible
                lending practices.
            </p>

            <div className={styles.openBankingButtonContainer}>
                <a
                    href={buttonHref}
                    className={`${styles.openBankingButton} ${isClicked ? styles.clicked : ''}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleButtonClick}
                >
                    {isClicked ? (
                        <>
                            <svg
                                className={styles.checkIcon}
                                width="20"
                                height="20"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                    fill="currentColor"
                                />
                            </svg>
                            Clicked
                        </>
                    ) : (
                        'Connect Open Banking'
                    )}
                </a>
            </div>

            {/* Copyable link field */}
            <div className={styles.copyLinkContainer}>
                <p className={styles.copyLinkLabel}>If the button doesn't work, copy this link:</p>
                <div className={styles.copyLinkField}>
                    <input type="text" value={buttonHref} readOnly className={styles.copyLinkInput} />
                    <button
                        onClick={handleCopyLink}
                        className={`${styles.copyButton} ${copySuccess ? styles.copySuccess : ''}`}
                        type="button"
                    >
                        {copySuccess ? (
                            <>
                                <svg
                                    className={styles.checkIcon}
                                    width="16"
                                    height="16"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z"
                                        fill="currentColor"
                                    />
                                </svg>
                                Copied!
                            </>
                        ) : (
                            'Copy'
                        )}
                    </button>
                </div>
            </div>

            <div className={styles.openBankingInfo}>
                <h3>Why we need Open Banking</h3>
                <p>To prove your lender failed you, we need your transaction dates</p>
                <p>
                    But don't worry — it's completely safe. We use Open Banking to get a secure, read-only view of your
                    bank history.
                </p>

                <div className={styles.infoCards}>
                    <div className={styles.infoCard}>
                        <h4 className={styles.cardTitle}>This lets us see:</h4>
                        <ul className={styles.cardList}>
                            <li>What your income and expenses were at the time</li>
                            <li>
                                Whether the lender should have known you were struggling to pay or had a high amount of
                                debt already
                            </li>
                        </ul>
                    </div>

                    <div className={styles.infoCard}>
                        <h4 className={styles.cardTitle}>Security features:</h4>
                        <ul className={styles.cardList}>
                            <li>You control what's shared</li>
                            <li>Bank-level encryption</li>
                            <li>No passwords or full access shared</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    )
}
